import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fmtINR, todayStr } from '../utils';

const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

function fmtAmt(n) { if (n == null) return ''; return fmtINR(n) || ''; }

const VCH_TYPES  = ['All','Sale','Purchase','Receipt','Payment','Journal','Contra','Debit Note','Sale Return','Purchase Return','Credit Note'];
const TYPE_COLOR = {
  'Sale':'var(--green)','Receipt':'var(--blue)','Purchase Return':'var(--green)',
  'Contra':'var(--purple)','Purchase':'var(--red)','Payment':'var(--amber)',
  'Sale Return':'var(--red)','Journal':'var(--amber)','Debit Note':'var(--amber)','Credit Note':'var(--amber)',
};

const PFX = 'db2_';
function getP(key) { return new URLSearchParams(window.location.search).get(PFX + key) || ''; }
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = val || '';
  if (v && v !== 'All') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearAllParams() {
  const sp = new URLSearchParams(window.location.search);
  ['tp','s'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

export default function DayBook({ db }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const [date,       setDateRaw]       = useState(() => getP('dt') || todayStr());
  const [typeFilter, setTypeFilterRaw] = useState(() => getP('tp') || 'All');
  const [search,     setSearchRaw]     = useState(() => getP('s'));

  const setDate       = v => { setDateRaw(v);       setP('dt', v); };
  const setTypeFilter = v => { setTypeFilterRaw(v); setP('tp', v); };
  const setSearch     = v => { setSearchRaw(v);     setP('s',  v); };

  // Use a ref so fetchData always sees latest date without being recreated on every date change
  const dateRef = useRef(date);
  useEffect(() => { dateRef.current = date; }, [date]);

  const fetchData = useCallback(async (d) => {
    const targetDate = d ?? dateRef.current;
    if (!db || !targetDate) return;
    setLoading(true); setError(null); setData(null);
    try {
      const res  = await fetch(`${API}/api/day-book?date=${targetDate}&db=${encodeURIComponent(db)}`,
        { headers: { Authorization: `Bearer ${token()}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Request failed');
      setData(json.data || json);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [db]); // only depends on db — date is read from ref

  // Fire when db first becomes available OR when date changes
  const prevDb = useRef('');
  useEffect(() => {
    if (!db) return;
    // on db change: refetch current date
    if (prevDb.current !== db) {
      prevDb.current = db;
      fetchData(dateRef.current);
      return;
    }
    fetchData(dateRef.current);
  }, [db, date, fetchData]); // eslint-disable-line

  const entries = useMemo(() => {
    if (!data?.entries) return [];
    let rows = data.entries;
    if (typeFilter !== 'All') rows = rows.filter(r => r.VchType === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        (r.Particulars||'').toLowerCase().includes(q) ||
        (r.VchNo||'').toLowerCase().includes(q) ||
        (r.Narration||'').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, typeFilter, search]);

  const grouped = useMemo(() => {
    const groups = []; let cur = null;
    for (const row of entries) {
      if (!cur || cur.VchCode !== row.VchCode) {
        cur = { VchCode:row.VchCode, VchType:row.VchType, VchNo:row.VchNo, Narration:row.Narration, rows:[] };
        groups.push(cur);
      }
      cur.rows.push(row);
    }
    return groups;
  }, [entries]);

  const opening = data?.opening || {};
  const totals  = data?.totals  || {};

  const anyFilter = typeFilter !== 'All' || !!search.trim();
  function clearAll() {
    setTypeFilterRaw('All'); setSearchRaw('');
    clearAllParams();
  }

  return (
    <div className="page-wrap fade-up">
      <div className="page-head">
        <div className="page-title">Day Book</div>
        <div className="page-sub">Daily voucher ledger · {db}</div>
      </div>

      {/* ── Top bar */}
      <div className="db-topbar">
        <div className="db-topbar-left">
          <input type="date" className="filter-input db-date-input"
            value={date} onChange={e => setDate(e.target.value)} />
          <input className="filter-input db-search-input"
            placeholder="Search particulars, voucher…"
            value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn-reset" onClick={clearAll} disabled={!anyFilter}>↺ Reset</button>
        </div>
        <button
          className={`btn-refresh${loading ? ' spinning' : ''}`}
          onClick={() => fetchData(dateRef.current)}
          disabled={loading}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* ── Type filter chips */}
      <div className="db-chips-row">
        {VCH_TYPES.map(t => (
          <button key={t} className={`db-chip${typeFilter === t ? ' db-chip-active' : ''}`}
            onClick={() => setTypeFilter(t)}>{t}</button>
        ))}
      </div>

      {/* ── Balance strip */}
      {data && (
        <div className="db-bal-scroll-wrap">
          <div className="db-bal-strip-scroll">
            <BalCell label="Opening"      value={opening.OpeningBalance} side={opening.Side} />
            <BalCell label="Cash In"      value={totals.TotalCashIn}     color="var(--green)" />
            <BalCell label="Cash Out"     value={totals.TotalCashOut}    color="var(--red)" />
            <BalCell label="Total Debit"  value={totals.TotalDebit} />
            <BalCell label="Total Credit" value={totals.TotalCredit} />
            <BalCell label="Closing"      value={totals.ClosingBalance}  side={totals.ClosingBalanceSide} bold />
          </div>
        </div>
      )}

      {error && (
        <div className="error-bar">
          <span className="error-icon">⚠</span>
          <div>
            <div>Failed to load</div>
            <div className="error-detail">{error}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner" /><span>Loading…</span></div>
      ) : data ? (
        <div className="table-wrap page-table-wrap">
          <table className="data-table" style={{ minWidth:680 }}>
            <thead>
              <tr>
                <th style={{ width:80 }}>Type</th>
                <th style={{ width:110 }}>Voucher</th>
                <th>Particulars</th>
                <th className="num-col" style={{ width:100 }}>Cash In</th>
                <th className="num-col" style={{ width:100 }}>Cash Out</th>
                <th className="num-col" style={{ width:100 }}>Debit</th>
                <th className="num-col" style={{ width:100 }}>Credit</th>
                <th style={{ width:140 }}>Narration</th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="empty-state">
                    <div className="empty-icon">📒</div>
                    <h3>No entries</h3><p>No vouchers found for this date</p>
                  </div>
                </td></tr>
              ) : grouped.map((grp, gi) =>
                grp.rows.map((row, ri) => (
                  <tr key={`${grp.VchCode}-${ri}`}
                    style={{ borderTop: ri===0 && gi>0 ? '2px solid var(--border)' : undefined }}>
                    {ri === 0 && (
                      <td rowSpan={grp.rows.length} style={{ verticalAlign:'top', paddingTop:10 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:TYPE_COLOR[grp.VchType]||'var(--text3)' }}>{grp.VchType}</span>
                      </td>
                    )}
                    {ri === 0 && (
                      <td rowSpan={grp.rows.length} style={{ verticalAlign:'top', paddingTop:10, fontFamily:'var(--mono)', fontSize:12, color:'var(--text2)' }}>
                        {grp.VchNo || '—'}
                      </td>
                    )}
                    <td style={{ paddingLeft: ri>0 ? 24 : undefined }}>{row.Particulars || <span className="muted">—</span>}</td>
                    <td className="num-col" style={{ color:'var(--green)' }}>{row.CashIn  != null ? fmtAmt(row.CashIn)  : ''}</td>
                    <td className="num-col" style={{ color:'var(--red)' }}>  {row.CashOut != null ? fmtAmt(row.CashOut) : ''}</td>
                    <td className="num-col">{row.DebitAmt  != null ? fmtAmt(row.DebitAmt)  : ''}</td>
                    <td className="num-col">{row.CreditAmt != null ? fmtAmt(row.CreditAmt) : ''}</td>
                    {ri === 0 && (
                      <td rowSpan={grp.rows.length} style={{ verticalAlign:'top', paddingTop:10, fontSize:12, color:'var(--text3)' }}>
                        {grp.Narration || ''}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : !error ? (
        <div className="empty-state"><div className="empty-icon">📒</div><h3>Select a date</h3><p>Day book will load here</p></div>
      ) : null}
    </div>
  );
}

function BalCell({ label, value, side, color, bold }) {
  const col = color || (side==='Dr' ? 'var(--blue)' : side==='Cr' ? 'var(--purple)' : 'var(--text)');
  return (
    <div className="db-bal-cell">
      <div className="db-bal-label">{label}</div>
      <div className="db-bal-value" style={{ fontWeight:bold?700:500, color:col }}>
        {value != null ? fmtAmt(value) : '—'}
        {side && <span style={{ fontSize:9, marginLeft:3, opacity:0.65 }}>{side}</span>}
      </div>
    </div>
  );
}
